import React, { useEffect } from 'react';
import { compose, map, find, propEq, sort } from 'ramda';
import {
  reduxForm,
  InjectedFormProps,
  Form,
  formValueSelector,
} from 'redux-form';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import WizardCard from 'components/WizardCard/WizardCard';
import Fields from 'components/Fields';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { PreviousStepType } from 'components/Wizard/Wizard';
import api from 'api/api';
import wizard from 'consts/wizard';
import IntlProvider, {
  useFormatMessage,
  IntlProviderType,
  getLabelByIntlPrefix,
} from 'components/IntlProvider';

import { validate, wizardStyles } from './wizardUtils';

const { connect, selectors, actions } = StoreProvider;

interface CategoryType {
  id: number;
  category: string;
  en_category: string;
}

interface ProductGroupProps extends PreviousStepType {
  subcategories: CategoryType[];
  category: CategoryType;
  setSubcategories: (data: object) => void;
  intl: IntlProviderType;
}

const ProductSubGroup = ({
  handleSubmit,
  error,
  subcategories,
  category,
  previousStep,
  setSubcategories,
}: ProductGroupProps & InjectedFormProps) => {
  const classes = wizardStyles();
  const t = useFormatMessage();
  useEffect(() => {
    if (category && category.id !== wizard.OTHER_CATEGORY_ID) {
      api.resources
        .getAssistantSubcategories({
          categoryId: category.id,
        })
        .then(({ data }) => {
          setSubcategories(data);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WizardCard>
      <Form onSubmit={handleSubmit} className={classes.form} noValidate>
        <Grid item xs={12}>
          <Typography
            component="h1"
            variant="h5"
            color="inherit"
            className={classes.title}
          >
            {t(
              'forms.wizard.productgroup',
              'Give us some information about your product'
            )}
          </Typography>
          {category.id !== wizard.OTHER_CATEGORY_ID ? (
            <Fields.Wizard.Subcategory
              category={category}
              subcategories={subcategories}
            />
          ) : (
            <Fields.Wizard.OtherCategory category={category} />
          )}

          {error && <FormHelperText error>{error}</FormHelperText>}
          <Button
            classes={{
              root: classes.submitButton,
            }}
            type="submit"
            variant="contained"
            color="primary"
          >
            {t('fields.wizard.common.continue', 'Continue')}
          </Button>
          <Button
            onClick={previousStep}
            classes={{
              root: classes.secondaryButton,
            }}
            color="secondary"
          >
            {t('fields.wizard.common.back', 'Back')}
          </Button>
        </Grid>
      </Form>
    </WizardCard>
  );
};

const wizardSelector = formValueSelector('wizard');

export default compose(
  IntlProvider.withIntl,
  connect(
    (state, props: ProductGroupProps) => ({
      category: find(
        propEq('id', parseInt(wizardSelector(state, 'category'), 10))
      )(selectors.assistantSelectors.categories(state)),
      subcategories: compose(
        map((category: CategoryType) => ({
          ...category,
          value: category.id.toString(),
          label: getLabelByIntlPrefix(category, props.intl.locale, 'category'),
        })),
        sort((a: CategoryType, b: CategoryType) => a.id - b.id)
      )(selectors.assistantSelectors.subcategories(state)),
    }),
    {
      setSubcategories:
        actions.assistantActions.setAssistantCategorySubcategories,
    }
  ),
  reduxForm({
    form: 'wizard',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate,
  })
)(ProductSubGroup);
